/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import produce from "immer";
import { useToasts } from "react-toast-notifications";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import interrogationObject from "../../assets/interrogationObject.json";
import { LayoutContext } from "../../components/layout";
import { PatientsContext } from "../../pages/patients";
import fetchHelper from "../../services/fetchHelper";
import { STATUS as statusDevice } from "../../constants/DevicesConstants";
import { INITIAL_FILE_LIST } from "./constants";
import UploadManufacturer from "./UploadManufacturer";

const UploadIRModal = ({ patientId }) => {
  const { user, intrOrderInfo, setIntrOrderInfo } = useContext(LayoutContext);
  const { devices, fetchDeviceInfo } = useContext(PatientsContext);
  const { addToast } = useToasts();
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [manufacturer, setManufacturer] = useState("");
  const [manType, setManType] = useState("");
  const [physicians, setPhysicians] = useState([]);
  const [device, setDevice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [interrogation, setInterrogation] = useState(interrogationObject);
  const [fileList, setFileList] = useState(INITIAL_FILE_LIST);
  const [patient, setPatient] = useState(null);
  const isMRNDisable = useRef(false);
  const history = useHistory();
  const isModalOpen = useRef(null);
  const cancelButton = useRef(null);
  const [cancelRequest, setCancelRequest] = useState(new AbortController());
  const [shouldShowFileFormatError, setShouldShowFileFormatError] = useState(false);
  const [errorIndex, setErrorIndex] = useState({});
  const [shouldDisableUploadBtn, setShouldDisableUploadBtn] = useState(true);
  const [disabledFinishUpload, setDisabledFinishUpload] = useState(true);
  isModalOpen.current = Boolean(history.location.hash);

  useEffect(() => {
    fetchReadPhysicians();
    if (fetchDeviceInfo) {
      fetchDeviceInfo();
    }
  }, []);

  useEffect(() => {
    setErrorIndex({});
  }, [manufacturer]);

  useEffect(() => {
    setShouldShowFileFormatError(Object.keys(errorIndex).some((key) => errorIndex[key]));
  }, [errorIndex]);

  useEffect(() => {
    if (patient) {
      if (patient.patientLocation && patient.patientLocation.length > 0) {
        isMRNDisable.current = !patient?.patientLocation[0]?.mrn ? false : true;
        setInterrogation((prev) => {
          return produce(prev, (draft) => {
            draft.interrogation.routingLocId = patient?.patientLocation[0]?.location?.id;
            draft.patientLocation = {
              id: patient?.patientLocation[0]?.id,
              location: {
                id: patient?.patientLocation[0]?.location?.id,
              },
              mrn: patient?.patientLocation[0]?.mrn,
            };
            draft.patient = patient?.patientLocation[0]?.patient;
            delete draft.patient.patientState;
          });
        });
      }
    }

    setInterrogation((prev) =>
      produce(prev, (draft) => {
        draft.patient = { ...patient };
        if (draft.patient?.dob) draft.patient.dob = dayjs(draft.patient.dob).toDate();
      })
    );
  }, [patient]);

  useEffect(() => {
    setInterrogation((prev) =>
      produce(prev, (draft) => {
        if (intrOrderInfo?.irVisitNumber) {
          draft.interrogation.visitNumber = intrOrderInfo?.irVisitNumber;
        }
      })
    );
  }, [intrOrderInfo]);

  useEffect(() => {
    switch (manufacturer) {
      case "Biotronik":
        setManType("bio");
        break;
      case "BostonScientific":
        setManType("bos");
        break;
      case "Medtronic":
        setManType("med");
        break;
      case "St.Jude":
        setManType("sjm");
        break;
    }
  }, [manufacturer]);

  useEffect(() => {
    if (!manufacturer) {
      setShouldDisableUploadBtn(true);
      return;
    }
    const shouldDisable =
      !fileList[manufacturer].every((item) => item.file) || isLoading || shouldShowFileFormatError;
    setShouldDisableUploadBtn(shouldDisable);
  }, [manufacturer, isLoading, shouldShowFileFormatError, fileList]);

  const fetchReadPhysicians = async () => {
    try {
      const lS = window.localStorage.getItem("genToken");
      const url = "/patientmanagement/rest/users/available-physicians";
      const response = await fetch(url, {
        headers: {
          token: JSON.parse(lS)["token"],
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const json = await response.json();
        setPhysicians(json);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onUpload = async () => {
    try {
      setIsLoading(true);
      const formData = new FormData();

      fileList[manufacturer].forEach((item, index) => {
        if (item.file) {
          formData.append(`file${index + 1}`, item.file, item.name);
        }
      });

      const response = await fetchHelper.post(
        `/patientmanagement/rest/uploads/uploadPoc/${manType}`,
        formData,
        null,
        false,
        {
          signal: cancelRequest.signal,
        }
      );

      const intervalSuccess = setInterval(async () => {
        if (!isModalOpen.current) {
          return clearInterval(intervalSuccess);
        }

        const { id } = response;
        const iruploadResponse = await fetchHelper.get({
          url: `/patientmanagement/rest/queue/irupload/${manType}/${id}/irprocessed`,
          options: {
            signal: cancelRequest.signal,
          },
        });

        clearInterval(intervalSuccess);
        const { interrogation, errors } = iruploadResponse;
        if (errors) {
          errors.forEach((err) => {
            addToast(err.msg, {
              appearance: "error",
              autoDismiss: false,
            });
          });
          return cancelButton.current.click();
        }

        setInterrogation(
          produce({ interrogation }, (draft) => {
            draft.interrogation.interrogationStatus = "NOTASSIGNED";
            draft.interrogation.orderQ = { id: null };

            if (intrOrderInfo?.irId) {
              draft.interrogationOrderId = intrOrderInfo?.irId;
              draft.interrogation.interrogationDate = intrOrderInfo?.irDate;
              draft.interrogation.visitNumber = intrOrderInfo?.irVisitNumber;
            }
          })
        );

        setPatient(
          produce(interrogation.patient, (draft) => {
            draft["name"] = draft["firstName"];
            draft["family"] = draft["lastName"];
            if (draft["dob"]) draft["dob"] = dayjs(draft["dob"]).toDate();
            delete draft["firstName"];
            delete draft["lastName"];
          })
        );
        enableFinishUpload(interrogation.patient);
        setDevice({
          patient: {
            id: patientId,
          },
          deviceType: interrogation?.device?.type,
          manufacturerName: interrogation?.device?.manufacturer.toUpperCase(),
          modelName: interrogation?.device?.modelName,
          serialNumber: interrogation?.device?.serialNumber,
          implantDate: null,
          status: interrogation?.device?.status || statusDevice[0],
        });

        const deviceResp = await fetchHelper.getTypeTextJson({
          url: `/patientmanagement/rest/patients/findByDevice?serialNumber=${interrogation?.device?.serialNumber}`,
          options: {
            signal: cancelRequest.signal,
          },
        });

        setIsLoading(false);
        setShowUploadForm(true);
        if (deviceResp) {
          if (deviceResp.patientLocation && deviceResp.patientLocation.length > 0) {
            enableFinishUpload(deviceResp?.patientLocation[0]?.patient);
          }
          deviceResp.dob = dayjs(deviceResp.dob).toDate();
          setPatient(deviceResp);
        }
      }, 2500);
    } catch (error) {
      setIsLoading(false);
      if (error?.name !== "AbortError") {
        const { errors } = error;
        errors &&
          errors.forEach((err) => {
            addToast(err.msg, {
              appearance: "error",
              autoDismiss: false,
            });
          });
      }
    }
  };

  const onFinishUpload = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("genToken") && JSON.parse(localStorage.getItem("genToken")).token;
      const resp = await fetch(`/patientmanagement/rest/interrogations/submit`, {
        method: "POST",
        headers: { token, "Content-Type": "application/json", "user-interaction": "sync" },
        body: JSON.stringify(interrogation),
      });
      if (resp.ok) {
        const { id, correlationId } = await resp.json();

        if (correlationId) {
          const response = await fetchHelper.get({
            url: `/patientmanagement/rest/PdfGenerator/${correlationId}/status`,
          });
          if (response?.status === "SUCCESSFUL") {
            setIsLoading(false);
            setIntrOrderInfo({});
            history.push(`/dashboard3/${id}`);
          } else {
            addToast(`There was a problem while generating the pdf for the interrogation id ${id}.`, {
              appearance: "error",
            });
          }
        } else {
          setIsLoading(false);
          setIntrOrderInfo({});
          history.push(`/dashboard3/${id}`);
        }
      }
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
      resetForms();
    }
  };

  const onFileChange = (e, targetFileIndex) => {
    const { files } = e.target;
    if (!files.length) {
      return;
    }
    const fileExtension = files[0].name.split(".").pop();
    const index = fileList[manufacturer].findIndex((e) => e.format === fileExtension);

    const targetManufacturer = fileList[manufacturer];
    if (index !== -1 && fileList[manufacturer][targetFileIndex]["format"] === fileExtension) {
      targetManufacturer[targetFileIndex].file = files[0];
      setErrorIndex({ ...errorIndex, [targetFileIndex]: false });
    } else {
      targetManufacturer[targetFileIndex].file = null;
      setErrorIndex({ ...errorIndex, [targetFileIndex]: true });
    }
    setFileList({ ...fileList, [manufacturer]: targetManufacturer });
  };

  const setPatientInfo = (e) => {
    const { value, name } = e.target;
    setInterrogation((prev) => {
      return produce(prev, (draft) => {
        if (!draft.patient) draft.patient = {};
        draft.patient[name] = value;
      });
    });
  };

  const setInterrogationInfo = (e) => {
    const { value, name } = e.target;
    setInterrogation((prev) => {
      return produce(prev, (draft) => {
        if (name === "mrn") {
          if (!draft["patientLocation"]) draft["patientLocation"] = {};
          draft.patientLocation[name] = value;
        } else if (name === "location") {
          if (!draft["patientLocation"]) draft["patientLocation"] = {};
          draft.interrogation["routingLocId"] = value;
          draft.patientLocation["id"] = null;
          draft.patientLocation[name] = { id: value };
        } else {
          draft.interrogation[name] = value;
        }
      });
    });
  };

  const resetForms = () => {
    isModalOpen.current = false;
    cancelRequest.abort();
    setCancelRequest(new AbortController());
    setManufacturer("");
    setShowUploadForm(false);
    setIsLoading(false);
    setInterrogation(interrogationObject);
    setIntrOrderInfo({});
    setPatient(null);
    // clean the INITIAL_FILE_LIST to the initial state
    Object.keys(INITIAL_FILE_LIST).forEach(function (manufacture) {
      INITIAL_FILE_LIST[manufacture].forEach(function (item, index, theArray) {
        theArray[index].file = null;
      });
    });
    setFileList(INITIAL_FILE_LIST);
    setManType("");
    setShouldShowFileFormatError(false);
    setErrorIndex({});
  };

  const enableFinishUpload = (patient) => {
    if (patientId && patient?.id) {
      if (Number(patient.id) !== Number(patientId)) {
        setDisabledFinishUpload(true);
        addToast("Upload cannot be completed. Uploaded file does not match patient profile information.", {
          appearance: "error",
          autoDismiss: false,
        });
      } else {
        setDisabledFinishUpload(false);
      }
    } else {
      setDisabledFinishUpload(false);
    }
  };

  return (
    <>
      <div className="modal override-row up-interrog popup" id="uploadIR">
        <div className="modalContent">
          <div className="grpHdr">
            <h3>Upload Report</h3>
          </div>
          {showUploadForm ? (
            <>
              <div className="row rowStretch user-personal-info">
                <div className="col col-s-100 col-m-50 col50">
                  <label className="inCal">
                    <span>First Name</span>
                    <input
                      type="text"
                      name="name"
                      value={interrogation?.patient?.name || ""}
                      onChange={setPatientInfo}
                    />
                  </label>
                </div>
                <div className="col col-s-100 col-m-50 col50">
                  <label className="inCal">
                    <span>Last Name</span>
                    <input
                      type="text"
                      name="family"
                      value={interrogation?.patient?.family || ""}
                      onChange={setPatientInfo}
                    />
                  </label>
                </div>
                <div className="col col-s-100 col-m-50 col50">
                  <label className="inCal">
                    <span>Date of Birth</span>
                  </label>
                  <DatePicker
                    selected={interrogation?.patient?.dob || ""}
                    onChange={(date) => {
                      setInterrogation((prev) => {
                        return produce(prev, (draft) => {
                          draft.patient["dob"] = date;
                        });
                      });
                    }}
                  />
                </div>

                <div className="col col-s-100 col50">
                  <div className="containerFlex containerFormLi" style={{ flexDirection: "column" }}>
                    <label className="inCal">
                      <span>Gender</span>
                      <select name="gender" onChange={setPatientInfo} value={interrogation?.patient?.gender}>
                        <option value="">Search Gender...</option>
                        <option value="MALE">Male</option>
                        <option value="FEMALE">Female</option>
                        <option value="UNKNOWN">Unknown</option>
                        <option value="NONBINARY">Nonbinary</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div className="col col-s-100 col-m-50 col50">
                  <label className="inCal">
                    <span>Location</span>
                    <select
                      name="location"
                      onChange={setInterrogationInfo}
                      value={interrogation?.patientLocation?.location?.id}
                    >
                      <option value="">Search by Location...</option>
                      <option value={user?.locations[0]?.id}>{user?.locations[0]?.name}</option>
                    </select>
                  </label>
                </div>
                <div className="col col-s-100 col-m-50 col50">
                  <label className="inCal">
                    <span>Medical Record Number</span>
                    <input
                      type="text"
                      name="mrn"
                      onChange={setInterrogationInfo}
                      value={interrogation?.patientLocation?.mrn || ""}
                      disabled={isMRNDisable.current}
                    />
                  </label>
                </div>
                <div className="col col-s-100 col-m-50 col50">
                  <label className="inCal">
                    <span>Visit Number</span>
                    <input
                      type="text"
                      name="visitNumber"
                      onChange={setInterrogationInfo}
                      value={
                        interrogation?.interrogation?.visitNumber
                          ? interrogation?.interrogation?.visitNumber
                          : ""
                      }
                      disabled={!!intrOrderInfo?.irVisitNumber}
                    />
                  </label>
                </div>
                <div className="col col-s-100 col-m-50 col50">
                  <label className="inCal">
                    <span>Ordering physicians</span>
                    <select
                      name="orderingPhysician"
                      onChange={(e) => {
                        const { value } = e.target;
                        setInterrogation((prev) => {
                          return produce(prev, (draft) => {
                            draft.interrogation.orderingPhysician = {
                              id: value,
                            };
                          });
                        });
                      }}
                      value={interrogation?.interrogation?.orderingPhysician?.id}
                    >
                      <option value="">Select physicians</option>
                      {physicians
                        .sort((a, b) => {
                          if (a.family > b.family) return 1;
                          if (b.family > a.family) return -1;
                          return 0;
                        })
                        .map((user) => (
                          <option key={user.id} value={user.id}>
                            {`${user.family}, ${user.name}`}
                          </option>
                        ))}
                    </select>
                  </label>
                </div>
                <div className="col col-s-100 col-m-50 col50">
                  <label className="inCal">
                    <span>Reasons for Interrogation</span>
                    <input
                      type="text"
                      name="interrogationReason"
                      onChange={setInterrogationInfo}
                      value={interrogation?.interrogation?.interrogationReason || ""}
                    />
                  </label>
                </div>
                {devices ? (
                  <div className="col col-s-100 col-m-50 col50">
                    <label className="inCal">
                      {devices?.list?.findIndex((d) => d.serialNumber === device.serialNumber) >= 0 ? (
                        <>
                          <span>Device</span>
                          <select name="device">
                            <option value="">Select Device...</option>
                            {device &&
                              devices?.list
                                ?.filter((d) => d.serialNumber === device.serialNumber)
                                .map((dv, i) => (
                                  <option key={i} value={JSON.stringify(dv)}>
                                    {device.manufacturerName}
                                  </option>
                                ))}
                          </select>
                        </>
                      ) : (
                        <>
                          <span>New device detected</span>
                          <button className="btn btn-teal" style={{ width: "100%" }}>
                            {`${device.deviceType} - ${device.serialNumber}`}
                          </button>
                        </>
                      )}
                    </label>
                  </div>
                ) : null}
                <div className="col col-s-100 col-m-50 col50">
                  <label className="inCal">
                    <span>Interrogation Order ID</span>
                    <input type="text" value={intrOrderInfo?.irId} disabled />
                  </label>
                </div>
                <div className="col col-s-100 col-m-50 col50" />
              </div>
              <div className="row rowStretch modalAxnBtn">
                <div className="col create-ir-col" style={{ display: "flex" }}>
                  <button
                    className="btn btnSuccess"
                    onClick={onFinishUpload}
                    disabled={isLoading || disabledFinishUpload}
                  >
                    {isLoading ? (
                      <div
                        id="results-loading-icon"
                        className="saving-white-ico icon-64"
                        style={{ width: 15, height: 15, marginRight: 3 }}
                      />
                    ) : null}{" "}
                    Finish Upload
                  </button>
                  <a href="#" onClick={resetForms}>
                    <button className="btn btnLight">Cancel</button>
                  </a>
                </div>
              </div>
            </>
          ) : (
            <UploadManufacturer
              onUpload={onUpload}
              fileList={fileList}
              isLoading={isLoading}
              resetForms={resetForms}
              manufacturer={manufacturer}
              onFileChange={onFileChange}
              cancelButton={cancelButton}
              setManufacturer={setManufacturer}
              shouldDisableUploadBtn={shouldDisableUploadBtn}
              shouldShowFileFormatError={shouldShowFileFormatError}
            />
          )}
        </div>
      </div>
      <a href="#" className="close-popup"></a>
    </>
  );
};

export default UploadIRModal;
